import "./ResightingAssignments.css";
import { useContext, useEffect, useMemo, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppContext from "../../Context/AppContext";

function ResightingAdminDashboard() {
  const BASE_URL_LOC = process.env.REACT_APP_SERVER_LOCATION;
  const appStates = useContext(AppContext);
  const navigate = useNavigate();
  const [pipelineRows, setPipelineRows] = useState([]);
  const [internRows, setInternRows] = useState([]);
  const [blockedRows, setBlockedRows] = useState([]);
  const [onHoldRows, setOnHoldRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");

  const showToast = (message, severity = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const fetchAdminData = async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const authToken = appStates.getToken();
      if (!authToken) {
        setPipelineRows([]);
        setInternRows([]);
        setBlockedRows([]);
        setOnHoldRows([]);
        setTotalCount(0);
        setErrorMessage("Session expired. Please log in again.");
        return;
      }

      const requestOptions = {
        method: "GET",
        cache: "no-store",
        redirect: "follow",
        headers: {
          "authentication-token": authToken,
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Headers": "authentication-token",
          "Access-Control-Expose-Headers": "authentication-token",
        },
        mode: "cors",
      };

      const [statsResponse, blockedResponse, onHoldResponse] = await Promise.all([
        fetch(`${BASE_URL_LOC}/api/resighting/admin/stats`, requestOptions),
        fetch(`${BASE_URL_LOC}/api/resighting/admin/submissions`, requestOptions),
        fetch(`${BASE_URL_LOC}/api/resighting/admin/on-hold`, requestOptions),
      ]);

      const [statsBody, blockedBody, onHoldBody] = await Promise.all([
        statsResponse.json(),
        blockedResponse.json(),
        onHoldResponse.json(),
      ]);

      if (!statsResponse.ok) {
        setPipelineRows([]);
        setInternRows([]);
        setBlockedRows([]);
        setOnHoldRows([]);
        setTotalCount(0);
        setErrorMessage(statsBody?.error || "Unable to load admin stats right now.");
        return;
      }

      if (!blockedResponse.ok) {
        setBlockedRows([]);
        setErrorMessage(
          blockedBody?.error || "Unable to load blocked submissions right now."
        );
      }

      if (!onHoldResponse.ok) {
        setOnHoldRows([]);
        setErrorMessage(
          onHoldBody?.error || "Unable to load on-hold submissions right now."
        );
      }

      const stats = statsBody?.data || {};
      setPipelineRows(Array.isArray(stats?.pipeline?.states) ? stats.pipeline.states : []);
      setInternRows(Array.isArray(stats?.intern_progress) ? stats.intern_progress : []);
      setTotalCount(stats?.pipeline?.total || 0);
      setBlockedRows(Array.isArray(blockedBody?.data) ? blockedBody.data : []);
      setOnHoldRows(Array.isArray(onHoldBody?.data) ? onHoldBody.data : []);
    } catch (error) {
      setPipelineRows([]);
      setInternRows([]);
      setBlockedRows([]);
      setOnHoldRows([]);
      setTotalCount(0);
      setErrorMessage("Unable to load admin data right now.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [BASE_URL_LOC, appStates]);

  const handleReleaseSync = async () => {
    setIsSyncing(true);
    setErrorMessage("");

    try {
      const authToken = appStates.getToken();
      if (!authToken) {
        setErrorMessage("Session expired. Please log in again.");
        return;
      }

      const response = await fetch(
        `${BASE_URL_LOC}/api/resighting/release-resighting-records`,
        {
          method: "POST",
          redirect: "follow",
          headers: {
            "authentication-token": authToken,
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Headers": "authentication-token",
            "Access-Control-Expose-Headers": "authentication-token",
          },
          mode: "cors",
        }
      );

      const body = await response.json();

      if (!response.ok) {
        setErrorMessage(body?.error || "Unable to sync resighting records right now.");
        return;
      }

      const updatedCount = body?.data ?? 0;
      if (updatedCount > 0) {
        showToast("Synced records to ringing data.");
      } else {
        showToast("No new records matched ringing data.", "info");
      }
      await fetchAdminData();
    } catch (error) {
      setErrorMessage("Unable to sync resighting records right now.");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleResolveOpen = (row) => {
    const recordId = row?.resighting_id;
    if (!recordId) {
      return;
    }

    // navigate(`/home/resighting/admin/record/${recordId}`);
    window.open(`/home/resighting/admin/record/${recordId}`, '_blank', 'noopener, noreferrer');
  };

  const blockedQueueColumns = useMemo(
    () => [
      {
        headerName: "ID",
        valueGetter: (params) => params.data?.resighting_id ?? "-",
        sortable: true,
        filter: true,
        flex: 0.8,
        minWidth: 110,
      },
      {
        headerName: "RING NUMBER",
        valueGetter: (params) => params.data?.ring_number || "-",
        sortable: true,
        filter: true,
        flex: 0.8,
        minWidth: 110,
      },
      {
        headerName: "SPECIES",
        valueGetter: (params) => params.data?.scientific_name || "-",
        sortable: true,
        filter: true,
        flex: 1.25,
        minWidth: 180,
      },
      {
        headerName: "BLOCKED BY",
        valueGetter: (params) => params.data?.email || "-",
        sortable: true,
        filter: true,
        flex: 1.2,
        minWidth: 180,
      },
      {
        headerName: "REASON",
        valueGetter: (params) => params.data?.qc_remarks || "-",
        sortable: true,
        filter: true,
        flex: 1.8,
        minWidth: 240,
      },
      {
        headerName: "ACTION",
        field: "action",
        sortable: false,
        filter: false,
        minWidth: 130,
        maxWidth: 150,
        cellRenderer: (params) => (
          <button
            type="button"
            className="resighting-qc-inprogress-resume-btn"
            onClick={() => handleResolveOpen(params.data)}
          >
            Resolve
          </button>
        ),
      },
    ],
    []
  );

  const onHoldQueueColumns = useMemo(
    () => [
      {
        headerName: "ID",
        valueGetter: (params) => params.data?.resighting_id ?? "-",
        sortable: true,
        filter: true,
        flex: 0.8,
        minWidth: 110,
      },
      {
        headerName: "RING NUMBER",
        valueGetter: (params) => params.data?.ring_number || "-",
        sortable: true,
        filter: true,
        flex: 0.8,
        minWidth: 110,
      },
      {
        headerName: "SPECIES",
        valueGetter: (params) => params.data?.scientific_name || "-",
        sortable: true,
        filter: true,
        flex: 1.25,
        minWidth: 180,
      },
      {
        headerName: "INTERN EMAIL",
        valueGetter: (params) => params.data?.email || "-",
        sortable: true,
        filter: true,
        flex: 1.2,
        minWidth: 180,
      },
      {
        headerName: "REMARKS",
        valueGetter: (params) => params.data?.qc_remarks || "-",
        sortable: true,
        filter: true,
        flex: 1.8,
        minWidth: 240,
      },
      {
        headerName: "ACTION",
        field: "action",
        sortable: false,
        filter: false,
        minWidth: 130,
        maxWidth: 150,
        cellRenderer: (params) => (
          <button
            type="button"
            className="resighting-qc-inprogress-resume-btn"
            onClick={() => handleResolveOpen(params.data)}
          >
            Resolve
          </button>
        ),
      },
    ],
    []
  );

  const internProgressColumns = useMemo(
    () => [
      {
        headerName: "INTERN",
        valueGetter: (params) => params.data?.email || "-",
        sortable: true,
        filter: true,
        flex: 1.35,
        minWidth: 220,
      },
      {
        headerName: "IN PROGRESS",
        field: "in_progress",
        sortable: true,
        filter: true,
        flex: 0.9,
        minWidth: 100,
      },
      {
        headerName: "AWAITING REVIEW",
        field: "awaiting_review",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 180,
      },
      {
        headerName: "APPROVED",
        field: "approved",
        sortable: true,
        filter: true,
        flex: 0.8,
        minWidth: 130,
      },
      {
        headerName: "BLOCKED",
        field: "blocked",
        sortable: true,
        filter: true,
        flex: 0.8,
        minWidth: 80,
      },
      {
        headerName: "ON HOLD",
        field: "on_hold",
        sortable: true,
        filter: true,
        flex: 0.8,
        minWidth: 80,
      },
      {
        headerName: "TOTAL SUBMISSIONS",
        field: "total_submissions",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 190,
      },
    ],
    []
  );

  return (
    <div className="resighting-qc-page">
      {errorMessage && (
        <Alert severity="error" className="resighting-qc-alert">
          {errorMessage}
        </Alert>
      )}
      <section className="resighting-admin-section" style={{marginTop: "0"}}>
        <div className="resighting-admin-section-header">
          <h2 className="resighting-admin-heading">Pipeline</h2>
          <button
            type="button"
            className="resighting-qc-btn resighting-admin-sync-btn"
            onClick={handleReleaseSync}
            disabled={isSyncing || isLoading}
          >
            {isSyncing ? "Syncing..." : "Sync records"}
          </button>
        </div>
        <div className="resighting-admin-table-wrap">
          <table className="resighting-admin-table">
            <thead>
              <tr>
                <th className="resighting-admin-th">STATE</th>
                <th className="resighting-admin-th resighting-admin-align-right">COUNT</th>
                <th className="resighting-admin-th resighting-admin-align-right">% OF TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={3}>Loading admin stats...</td>
                </tr>
              )}
              {!isLoading && pipelineRows.map((row) => (
                <tr key={row.state}>
                  <td>{row.state}</td>
                  <td className="resighting-admin-align-right">{row.count}</td>
                  <td className="resighting-admin-align-right">{row.percent}</td>
                </tr>
              ))}
              <tr>
                <td className="resighting-admin-total-cell">Total</td>
                <td className="resighting-admin-total-cell resighting-admin-align-right">
                  {totalCount}
                </td>
                <td className="resighting-admin-total-cell resighting-admin-align-right">
                  -
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="resighting-admin-section">
        <h2 className="resighting-admin-heading">Intern progress</h2>
        <div className="resighting-admin-table-wrap">
          {isLoading && <p className="resighting-admin-grid-status">Loading intern progress...</p>}
          {!isLoading && internRows.length === 0 && (
            <p className="resighting-admin-grid-status">No intern progress found.</p>
          )}
          {!isLoading && internRows.length > 0 && (
            <div className="ag-theme-quartz resighting-admin-ag-grid">
              <AgGridReact
                rowData={internRows}
                columnDefs={internProgressColumns}
                pagination={false}
                domLayout="normal"
                rowHeight={44}
                headerHeight={40}
                defaultColDef={{
                  resizable: true,
                  editable: false,
                  sortable: true,
                  filter: true,
                }}
              />
            </div>
          )}
        </div>
      </section>

      <section className="resighting-admin-section">
        <h2 className="resighting-admin-heading">Blocked queue</h2>
        <p className="resighting-admin-paragraph" style={{ marginTop: "0.4rem" }}>
          Records where an intern hit a blocker. Open one to fix the data, add a
          resolution note, and send it back to the pool.
        </p>
        <div className="resighting-admin-table-wrap">
          {isLoading && <p className="resighting-admin-grid-status">Loading blocked queue...</p>}
          {!isLoading && blockedRows.length === 0 && (
            <p className="resighting-admin-grid-status">No blocked records. Nice.</p>
          )}
          {!isLoading && blockedRows.length > 0 && (
            <div className="ag-theme-quartz resighting-admin-ag-grid">
              <AgGridReact
                rowData={blockedRows}
                columnDefs={blockedQueueColumns}
                pagination={false}
                domLayout="normal"
                rowHeight={44}
                headerHeight={40}
                defaultColDef={{
                  resizable: true,
                  editable: false,
                  sortable: true,
                  filter: true,
                }}
              />
            </div>
          )}
        </div>
      </section>

      <section className="resighting-admin-section">
        <h2 className="resighting-admin-heading">On hold queue</h2>
        <p className="resighting-admin-paragraph" style={{ marginTop: "0.4rem" }}>
          Records parked by admin after review. Open one to inspect details and
          continue resolution.
        </p>
        <div className="resighting-admin-table-wrap">
          {isLoading && <p className="resighting-admin-grid-status">Loading on hold queue...</p>}
          {!isLoading && onHoldRows.length === 0 && (
            <p className="resighting-admin-grid-status">No on hold records.</p>
          )}
          {!isLoading && onHoldRows.length > 0 && (
            <div className="ag-theme-quartz resighting-admin-ag-grid">
              <AgGridReact
                rowData={onHoldRows}
                columnDefs={onHoldQueueColumns}
                pagination={false}
                domLayout="normal"
                rowHeight={44}
                headerHeight={40}
                defaultColDef={{
                  resizable: true,
                  editable: false,
                  sortable: true,
                  filter: true,
                }}
              />
            </div>
          )}
        </div>
      </section>

      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default ResightingAdminDashboard;
